import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api";
import { LayoutDashboard, Package, Store, ScanLine, CreditCard, LogOut, ExternalLink, Loader2, Tag, Ticket, Zap, UserCog, Users, ClipboardList, Settings, AlertTriangle, Building2, Mic2, Receipt, MapPin, Menu, X, BadgeCheck, Megaphone, Handshake } from "lucide-react";
import SalesTab from "./SalesTab";
import { enablePwa } from "../../pwa";
import InstallAppButton from "../../components/InstallAppButton";import ProductsTab from "./ProductsTab";
import IssuedVouchersTab from "./IssuedVouchersTab";
import OrdersTab from "./OrdersTab";
import CitiesTab from "./CitiesTab";
import StorefrontTab from "./StorefrontTab";
import RedeemTab from "./RedeemTab";
import StripeTab from "./StripeTab";
import DiscountsTab from "./DiscountsTab";
import BulkTab from "./BulkTab";
import WaitlistTab from "./WaitlistTab";
import AccountTab from "./AccountTab";
import SettingsTab from "./SettingsTab";
import StaffTab from "./StaffTab";
import PassesTab from "./PassesTab";
import MarketingTab from "./MarketingTab";
import VenuesTab from "./VenuesTab";
import ActsTab from "./ActsTab";
import RosterTab from "./RosterTab";

function OnboardingNudge({ onGo }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/business/connect/status"), api.get("/business/subscription/status")])
      .then(([c, s]) => setState({ method: c.data.payout_method, ready: !!c.data.payout_ready, sub: s.data.status === "active" }))
      .catch(() => {});
  }, []);
  if (!state) return null;
  const { method, ready, sub } = state;

  // Not set up to receive money yet — prominent prompt.
  if (!ready) {
    return (
      <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#16243b] to-[#20344f] text-white p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="payout-nudge-none">
        <div className="flex items-center gap-3">
          <Zap className="text-[#2ec9c9] shrink-0" size={22} />
          <div>
            <p className="font-semibold">Connect Stripe to receive payouts</p>
            <p className="text-sm text-white/70">You can build and list events now, but you won't get paid for ticket sales until a payout method is set up.</p>
          </div>
        </div>
        <button onClick={onGo} data-testid="nudge-cta" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium whitespace-nowrap">Set up payouts</button>
      </div>
    );
  }

  // Under the per-ticket plan there's no monthly subscription to activate.
  return null;
}

function OverdueInvoiceBanner({ onGo }) {
  const [overdue, setOverdue] = useState([]);
  useEffect(() => {
    api.get("/business/invoices").then(({ data }) => setOverdue((data || []).filter((i) => i.overdue))).catch(() => {});
  }, []);
  if (overdue.length === 0) return null;
  const total = overdue.reduce((s, i) => s + (i.amount || 0), 0);
  return (
    <div className="mb-6 rounded-2xl bg-red-50 border border-red-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="overdue-invoice-banner">
      <div className="flex items-center gap-3">
        <AlertTriangle className="text-red-500 shrink-0" size={22} />
        <div>
          <p className="font-semibold text-red-700">{overdue.length} overdue invoice{overdue.length === 1 ? "" : "s"} — £{total.toFixed(2)} outstanding</p>
          <p className="text-sm text-red-600/80">Please settle {overdue.length === 1 ? "it" : "them"} to keep your account in good standing.</p>
        </div>
      </div>
      <button onClick={onGo} data-testid="overdue-banner-cta" className="rounded-xl px-5 py-2.5 text-sm font-medium whitespace-nowrap bg-red-500 hover:bg-red-600 text-white transition-colors">View invoices</button>
    </div>
  );
}

const tabs = [
  { id: "sales", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Events", icon: Package },
  { id: "acts", label: "Artists", icon: Mic2 },
  { id: "roster", label: "Roster & bookings", icon: Handshake },
  { id: "venues", label: "Venues", icon: Building2 },
  { id: "cities", label: "Cities", icon: MapPin },
  { id: "issued", label: "Tickets", icon: Ticket },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "guestlist", label: "Guest list", icon: Users },
  { id: "waitlist", label: "Waitlist", icon: ClipboardList },
  { id: "discounts", label: "Promo codes", icon: Tag },
  { id: "storefront", label: "Storefront", icon: Store },
  { id: "redeem", label: "Check-in", icon: ScanLine },
  { id: "staff", label: "Door staff", icon: Users },
  { id: "passes", label: "Passes", icon: BadgeCheck },
  { id: "marketing", label: "Marketing", icon: Megaphone },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "account", label: "My account", icon: UserCog },
];

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || localStorage.getItem("gp_dash_tab") || "sales");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("gp_dash_tab", tab);
    const p = new URLSearchParams(window.location.search);
    p.set("tab", tab);
    window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
  }, [tab]);

  useEffect(() => {
    const b = user?.business;
    if (b?.pwa_enabled && b?.pwa_active) {
      enablePwa({
        manifestUrl: `/api/store/${b.slug}/manifest.json?mode=dashboard&origin=${encodeURIComponent(window.location.origin)}`,
        themeColor: b.pwa_theme_color || b.brand_color,
      });
    }
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "venue") return <Navigate to="/venue" replace />;

  const biz = user.business;
  const go = (id) => { setTab(id); setNavOpen(false); };

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-[#16243b] text-white flex items-center justify-between px-4 h-14">
        <span className="text-lg font-bold tracking-tight">TicketsPro.app</span>
        <button onClick={() => setNavOpen(true)} data-testid="dashboard-menu-btn" className="p-2 -mr-2" aria-label="Open menu"><Menu size={22} /></button>
      </div>

      {/* Backdrop (mobile) */}
      {navOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setNavOpen(false)} data-testid="dashboard-nav-backdrop" />}

      {/* Sidebar */}
      <aside className={`w-[260px] lg:w-[240px] bg-[#16243b] text-white flex flex-col fixed top-0 left-0 h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 flex items-center justify-between gap-2">
          <span className="text-2xl font-bold tracking-tight">TicketsPro.app</span>
          <button onClick={() => setNavOpen(false)} className="lg:hidden p-1 text-[#9fb0c8] hover:text-white" aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 px-3 space-y-1 mt-2 overflow-y-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => go(t.id)} data-testid={`nav-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#2ec9c9] text-white" : "text-[#9fb0c8] hover:bg-white/5 hover:text-white"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          {biz && (
            <a href={`/store/${biz.slug}`} target="_blank" rel="noreferrer"
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#9fb0c8] hover:text-white transition-colors">
              <ExternalLink size={16} /> View store
            </a>
          )}
          <button onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#9fb0c8] hover:text-white transition-colors">
            <LogOut size={16} /> Sign out
          </button>
          {biz?.pwa_enabled && biz?.pwa_active && (
            <InstallAppButton className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm text-[#16243b] bg-[#2ec9c9] hover:bg-[#28b4b4] font-medium transition-colors mt-1" label="Install app" />
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="lg:ml-[240px] p-4 sm:p-6 lg:p-8">
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl font-bold text-[#16243b]">{(tabs.find((t) => t.id === tab) || tabs[0]).label}</h1>
          <p className="text-[#5a6b80] text-sm mt-1">{biz?.name}</p>
        </div>
        {tab !== "payments" && <OnboardingNudge onGo={() => setTab("payments")} />}
        {tab !== "payments" && <OverdueInvoiceBanner onGo={() => setTab("payments")} />}
        {tab === "sales" && <SalesTab />}
        {tab === "issued" && <IssuedVouchersTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "cities" && <CitiesTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "acts" && <ActsTab />}
        {tab === "roster" && <RosterTab />}
        {tab === "venues" && <VenuesTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "guestlist" && <BulkTab />}
        {tab === "waitlist" && <WaitlistTab />}
        {tab === "storefront" && <StorefrontTab />}
        {tab === "redeem" && <RedeemTab />}
        {tab === "staff" && <StaffTab />}
        {tab === "passes" && <PassesTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "payments" && <StripeTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "account" && <AccountTab />}
      </main>
    </div>
  );
}
